<script setup lang="ts">
import { ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 参考历史案例给解决方案建议。
 *
 * 界面上刻意把「历史案例原文」和「AI 建议」分开摆：
 * 案例是公司真做过的事实（服务端原样带回，AI 不许转述），
 * 建议是模型的推测。两者混在一起，人就分不清哪句是有人验证过的。
 */
const props = defineProps<{
  issueId: number | string | null;
  title: string;
  description: string;
  partName?: string;
  productCategory?: string;
  category?: string;
  /** 当前表单里已经填了的根因/措施，用于判断要不要提示会覆盖 */
  currentRootCause?: string;
  currentCountermeasure?: string;
}>();
const emit = defineEmits<{ apply: [patch: Record<string, string>] }>();

interface ReferenceCase {
  id: number; code: string | null; title: string; projectNo: string | null;
  partName: string | null; description: string | null;
  rootCause: string; countermeasure: string; closedAt: string | null; reason: string;
  prevention: string | null; source: '8D' | '问题单';
}
interface Suggestion {
  cases: ReferenceCase[]; rootCause: string; countermeasure: string;
  verification: string; cautions: string; caseBaseSize: number;
}

const busy = ref(false);
const result = ref<Suggestion | null>(null);

async function run(): Promise<void> {
  busy.value = true;
  try {
    const payload = await eqsApi.issueSolutionAssist({
      issueId: props.issueId ? Number(props.issueId) : undefined,
      title: props.title,
      description: props.description,
      partName: props.partName,
      productCategory: props.productCategory,
      category: props.category,
    });
    result.value = payload as unknown as Suggestion;
    if (!result.value.cases.length) {
      notify('info', '没找到相似案例', `翻遍了 ${result.value.caseBaseSize} 条历史问题，没有真正对得上的。下面的建议只是通用思路，请自行判断。`);
    }
  } catch (caught) {
    notify('error', '查历史案例失败', errorMessage(caught));
  } finally {
    busy.value = false;
  }
}

/** 建议填进表单——不自动保存，人还要看过再点「保存修改」 */
function apply(): void {
  const suggestion = result.value;
  if (!suggestion) return;
  const patch: Record<string, string> = {};
  if (suggestion.rootCause) patch.rootCause = suggestion.rootCause;
  if (suggestion.countermeasure) patch.countermeasure = suggestion.countermeasure;
  if (!Object.keys(patch).length) return;
  emit('apply', patch);
  notify('success', '建议已填进表单', '还没保存：确认无误后点「保存修改」，不对就直接改掉。');
}

const willOverwrite = () => Boolean(
  (props.currentRootCause ?? '').trim() || (props.currentCountermeasure ?? '').trim(),
);
</script>

<template>
  <section class="solution-assist">
    <header>
      <div>
        <b>参考历史案例</b>
        <small>翻公司做过的问题，找相似的，给一版根因和措施的草稿。</small>
      </div>
      <button class="button button-secondary" type="button" :disabled="busy" @click="run">
        {{ busy ? 'AI 查找中…' : result ? '重新查找' : 'AI 参考历史案例' }}
      </button>
    </header>

    <template v-if="result">
      <p class="assist-scope">
        在 {{ result.caseBaseSize }} 条「根因和措施都写清楚了」的历史问题里找的。
        <template v-if="result.cases.length">挑出 {{ result.cases.length }} 条最像的：</template>
        <template v-else>没有找到真正对得上的案例。</template>
      </p>

      <ol v-if="result.cases.length" class="assist-cases">
        <li v-for="item in result.cases" :key="item.id">
          <header>
            <b>
              {{ item.code || `#${item.id}` }} · {{ item.title }}
              <!-- 走完 8D 的案例分析更结构化，值得让人一眼看出来 -->
              <em v-if="item.source === '8D'" class="assist-tag">走完 8D</em>
            </b>
            <small>
              <template v-if="item.projectNo">{{ item.projectNo }}</template>
              <template v-if="item.partName"> · {{ item.partName }}</template>
            </small>
          </header>
          <p class="assist-why">像在哪：{{ item.reason || '（AI 没说明）' }}</p>
          <dl>
            <dt>{{ item.source === '8D' ? '根因（D4）' : '当时的根因' }}</dt><dd>{{ item.rootCause }}</dd>
            <dt>{{ item.source === '8D' ? '永久措施（D5）' : '当时的措施' }}</dt><dd>{{ item.countermeasure }}</dd>
            <template v-if="item.prevention">
              <dt>预防（D6）</dt><dd>{{ item.prevention }}</dd>
            </template>
          </dl>
        </li>
      </ol>

      <div class="assist-draft">
        <h5>给这条问题的建议<em>AI 推测，不是结论</em></h5>
        <dl>
          <template v-if="result.rootCause"><dt>根因</dt><dd>{{ result.rootCause }}</dd></template>
          <template v-if="result.countermeasure"><dt>措施</dt><dd>{{ result.countermeasure }}</dd></template>
          <template v-if="result.verification"><dt>怎么验证</dt><dd>{{ result.verification }}</dd></template>
          <template v-if="result.cautions"><dt class="warn">照搬要注意</dt><dd class="warn">{{ result.cautions }}</dd></template>
        </dl>
        <button
          v-if="result.rootCause || result.countermeasure"
          class="button button-primary"
          type="button"
          @click="apply"
        >填进「根因」和「措施」</button>
        <small v-if="willOverwrite()" class="assist-overwrite">
          表单里已经有内容，点了会被覆盖——先复制走再点。
        </small>
      </div>
    </template>
  </section>
</template>

<style scoped>
.solution-assist { margin-top: 12px; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: #fbfcfe; }
.solution-assist > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; }
.solution-assist > header b { font-size: 13px; }
.solution-assist > header small { display: block; margin-top: 2px; color: var(--muted); font-size: 11px; }
.assist-scope { margin: 0; padding: 0 12px 8px; color: var(--muted); font-size: 12px; line-height: 1.7; }

.assist-cases { margin: 0; padding: 0 12px 10px; list-style: none; display: grid; gap: 8px; }
.assist-cases li { padding: 9px 11px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); }
.assist-cases li > header { display: grid; gap: 2px; margin-bottom: 5px; }
.assist-cases li > header b { font-size: 12px; line-height: 1.5; }
.assist-cases li > header small { color: var(--muted); font-size: 11px; }
.assist-why { margin: 0 0 6px; color: var(--blue); font-size: 12px; line-height: 1.6; }
.assist-tag { margin-left: 6px; padding: 1px 7px; border-radius: 999px; background: rgba(31, 154, 92, .12); color: var(--ok, #1f9a5c); font-style: normal; font-size: 11px; font-weight: 400; }

dl { display: grid; grid-template-columns: 74px minmax(0, 1fr); gap: 3px 10px; margin: 0; }
dt { color: var(--muted); font-size: 11px; }
dd { margin: 0; font-size: 12px; line-height: 1.7; white-space: pre-wrap; }
dt.warn, dd.warn { color: #8a5a00; }

.assist-draft { padding: 10px 12px 12px; border-top: 1px solid var(--line); background: var(--white); }
.assist-draft h5 { display: flex; align-items: center; gap: 8px; margin: 0 0 7px; font-size: 12px; }
.assist-draft h5 em { padding: 1px 7px; border-radius: 999px; background: #fdf6ec; color: #8a5a00; font-style: normal; font-size: 11px; font-weight: 400; }
.assist-draft .button { margin-top: 9px; }
.assist-overwrite { display: block; margin-top: 6px; color: #8a5a00; font-size: 11px; }
</style>
